import { useMemo, useState } from 'react';
import { Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { Style } from './Styles.jsx';

/**
 * Reemplazo de la libreria "react-data-grid" (v5, del ~2018) que se usaba en
 * un par de pantallas de Enganches para mostrar tablas de solo lectura
 * (Plan de Pagos, Pagos, Liquidaciones). Esa libreria es de la epoca de los
 * class components viejos de React: usa ReactDOM.findDOMNode (eliminado por
 * completo en React 19 -> "TypeError: ReactDOM.findDOMNode is not a
 * function") y muy probablemente tambien el Context API legado, tambien
 * eliminado en React 19. No hay forma segura de "parchear" eso desde afuera.
 *
 * Como en los 3 lugares donde se usaba todas las columnas tenian
 * editable: false (no se editaba nada ahi, solo se mostraba), alcanza con
 * una tabla MUI simple. Recibe el mismo formato de columnas que ya se
 * armaba en cada pantalla (columns={[{key, name}, ...]}) y un array de filas
 * (objetos planos con esas keys), para no tener que tocar como cada pantalla
 * arma sus datos.
 *
 * onRowClick(rowIndex, row) se llama al hacer click en una fila, igual que
 * antes se usaba onRowClick={rowKeyGetter} de react-data-grid (que recibia
 * el indice de la fila como primer argumento).
 *
 * "sortable" (opcional, default false) habilita orden por columna haciendo
 * click en el encabezado -- pensado para AccountPicker.jsx (buscador de
 * Cuenta), que lo pide. Es opt-in a proposito: los 3 usos existentes de este
 * componente (Plan de Pagos/Pagos/Liquidaciones en Enganches) no lo pasan,
 * asi que su comportamiento no cambia.
 *
 * Cada columna puede traer opcionalmente "render(value, row)" para
 * personalizar como se muestra la celda (ej. recortar un nombre largo con
 * "..." y mostrarlo completo en un tooltip) sin perder el valor "crudo" (el
 * que sigue en row[col.key]) que es el que se usa para ordenar.
 *
 * "centerContent" (opcional, default false) centra el contenido de las celdas del
 * cuerpo -- los encabezados (Style.DataGrid) ya vienen centrados, pero el cuerpo
 * (Style.fieldList) no traia textAlign, asi que quedaba a la izquierda por default
 * del navegador. Pensado para AnticipoPicker.jsx, que lo pide. Es opt-in a proposito:
 * los 3 usos existentes (Plan de Pagos/Pagos/Liquidaciones en Enganches) y
 * AccountPicker.jsx no lo pasan, asi que su alineacion actual no cambia.
 */
const SimpleDataGrid = ({ columns = [], rows = [], onRowClick, sortable = false, centerContent = false }) => {
    const [sort, setSort] = useState({ key: null, direction: 'asc' });

    const sortedRows = useMemo(() => {
        if (!sortable || !sort.key) return rows;
        const { key, direction } = sort;
        const factor = direction === 'asc' ? 1 : -1;
        return [...rows].sort((a, b) => (
            String(a?.[key] ?? '').localeCompare(String(b?.[key] ?? ''), 'es', { numeric: true, sensitivity: 'base' }) * factor
        ));
    }, [rows, sortable, sort]);

    const toggleSort = (col) => {
        if (!sortable) return;
        setSort((prev) => (
            prev.key === col.key
                ? { key: col.key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
                : { key: col.key, direction: 'asc' }
        ));
    };

    return (
        <Table size="small">
            <TableHead>
                <TableRow>
                    {columns.map(col => (
                        <TableCell
                            key={col.key}
                            style={{ ...Style.DataGrid, cursor: sortable ? 'pointer' : undefined, userSelect: sortable ? 'none' : undefined }}
                            onClick={() => toggleSort(col)}
                        >
                            {col.name}
                            {sortable && sort.key === col.key ? (sort.direction === 'asc' ? ' ▲' : ' ▼') : ''}
                        </TableCell>
                    ))}
                </TableRow>
            </TableHead>
            <TableBody>
                {sortedRows.map((row, index) => (
                    <TableRow
                        key={row.id ?? row.key ?? index}
                        hover={!!onRowClick}
                        onClick={onRowClick ? () => onRowClick(index, row) : undefined}
                        style={onRowClick ? { cursor: 'pointer' } : undefined}
                    >
                        {columns.map(col => (
                            <TableCell key={col.key} style={centerContent ? { ...Style.fieldList, textAlign: 'center' } : Style.fieldList}>
                                {col.render ? col.render(row[col.key], row) : row[col.key]}
                            </TableCell>
                        ))}
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    );
};

export default SimpleDataGrid;
